export interface IQueueDurationSpineRow {
  frameId: string
  durationSec: number
}

export interface IQueueDurationSpineAudit {
  published: readonly IQueueDurationSpineRow[]
  materialized: readonly IQueueDurationSpineRow[]
}

export interface IQueueDurationSpineMismatchStatus {
  suspected: boolean
  reason: 'frame_capped' | 'campaign_total' | null
  frameIndex: number
  frameId: string | null
  publishedDurationSec: number
  materializedDurationSec: number
  publishedCampaignTotalSec: number
  materializedCampaignTotalSec: number
}

/** Ignore sub-second drift from float serialization. */
export const QUEUE_DURATION_SPINE_EPSILON_SEC = 0.25

/** Only treat as erroneous cap when published slot was clearly long-form video/editorial. */
export const QUEUE_DURATION_SPINE_LONG_SLOT_SEC = 6

const INACTIVE: IQueueDurationSpineMismatchStatus = {
  suspected: false,
  reason: null,
  frameIndex: -1,
  frameId: null,
  publishedDurationSec: 0,
  materializedDurationSec: 0,
  publishedCampaignTotalSec: 0,
  materializedCampaignTotalSec: 0
}

export function buildQueueDurationSpineRows(
  frames: readonly { id: string, duration?: number | null }[]
): IQueueDurationSpineRow[] {
  return frames.map(row => ({
    frameId: row.id,
    durationSec: row.duration ?? 0
  }))
}

/**
 * Detect when materialized queue durations are shorter than registration-resolved spine
 * (e.g. operator master videos capped to house still length).
 */
export function detectQueueDurationSpineMismatch(
  audit: IQueueDurationSpineAudit | null | undefined
): IQueueDurationSpineMismatchStatus {
  if (audit == null) {
    return INACTIVE
  }
  const { published, materialized } = audit
  if (published.length === 0 || published.length !== materialized.length) {
    return INACTIVE
  }

  let publishedCampaignTotalSec = 0
  let materializedCampaignTotalSec = 0

  for (let i = 0; i < published.length; i++) {
    const pub = published[i]!
    const mat = materialized[i]!
    publishedCampaignTotalSec += pub.durationSec
    materializedCampaignTotalSec += mat.durationSec
    if (pub.frameId !== mat.frameId) {
      return INACTIVE
    }
    const deltaSec = pub.durationSec - mat.durationSec
    if (
      pub.durationSec >= QUEUE_DURATION_SPINE_LONG_SLOT_SEC
      && deltaSec > QUEUE_DURATION_SPINE_EPSILON_SEC
    ) {
      return {
        suspected: true,
        reason: 'frame_capped',
        frameIndex: i,
        frameId: pub.frameId,
        publishedDurationSec: pub.durationSec,
        materializedDurationSec: mat.durationSec,
        publishedCampaignTotalSec,
        materializedCampaignTotalSec
      }
    }
  }

  const hasLongPublished = published.some(row => row.durationSec > 10)
  if (
    hasLongPublished
    && materializedCampaignTotalSec + QUEUE_DURATION_SPINE_EPSILON_SEC < publishedCampaignTotalSec * 0.75
  ) {
    const first = published[0]!
    return {
      suspected: true,
      reason: 'campaign_total',
      frameIndex: 0,
      frameId: first.frameId,
      publishedDurationSec: first.durationSec,
      materializedDurationSec: materialized[0]?.durationSec ?? 0,
      publishedCampaignTotalSec,
      materializedCampaignTotalSec
    }
  }

  return {
    ...INACTIVE,
    publishedCampaignTotalSec,
    materializedCampaignTotalSec
  }
}
